import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import api from '../../services/api';
import html2canvas from 'html2canvas';
import { AuthContext } from '../../context/AuthContext';

import 'moment/locale/es';
moment.locale('es');
const localizer = momentLocalizer(moment);

const BRAND = '#b0005a';
const MOBILE_BREAKPOINT = 768;
const DURATIONS = [15, 30, 45, 60, 90, 120, 180];

/* ─────────────────────────────────────────
   Estilos (sobrescriben react-big-calendar y definen la UI propia)
───────────────────────────────────────── */
const AGENDA_CSS = `
  .rbc-calendar { font-family: 'Inter', 'Segoe UI', sans-serif; }
  .rbc-allday-cell { display: none !important; }
  .rbc-toolbar { display: none !important; }
  .rbc-time-view, .rbc-month-view { border: 1px solid #e5e7eb; border-radius: 12px; overflow: hidden; }
  .rbc-time-header { background: #f9fafb; }
  .rbc-header { padding: 6px 0 !important; border-bottom: 0 !important; }
  .rbc-label { font-size: 11px; font-weight: 600; color: #6b7280; padding-right: 8px; }
  .rbc-timeslot-group { min-height: 48px; border-bottom: 1px solid #e5e7eb !important; }
  .rbc-day-slot .rbc-time-slot { border-top: 1px dashed #f1f5f9 !important; }
  .rbc-today { background-color: rgba(176, 0, 90, 0.04) !important; }
  .rbc-slot-selection { background: rgba(176, 0, 90, 0.1); border: 1px solid ${BRAND}; }
  .rbc-event { border-radius: 8px !important; padding: 3px 6px !important; overflow: hidden !important; }
  .rbc-event:hover { z-index: 30 !important; box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important; }
  .rbc-event-label { display: none !important; }
  .rbc-event-content { font-size: 11px !important; height: 100% !important; }
  .rbc-month-view .rbc-event { padding: 1px 4px !important; min-height: 20px !important; border-radius: 4px !important; }

  .ag-page { height: 100vh; display: flex; flex-direction: column; padding: 20px; background: #f8f9fb; box-sizing: border-box; overflow: hidden; }
  .ag-card { flex: 1; min-height: 0; display: flex; flex-direction: column; background: white; border-radius: 16px; padding: 20px; box-shadow: 0 2px 16px rgba(0,0,0,0.05); }

  .ag-toolbar { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 16px; }
  .ag-title { flex: 1; min-width: 160px; }
  .ag-title h2 { margin: 0; font-size: 20px; font-weight: 800; color: #111827; line-height: 1.2; }
  .ag-title h2::first-letter { text-transform: uppercase; }
  .ag-title small { color: #9ca3af; font-size: 12px; font-weight: 600; text-transform: capitalize; }
  .ag-group { display: flex; border: 1px solid #e5e7eb; border-radius: 10px; overflow: hidden; }
  .ag-group button { padding: 7px 14px; border: none; background: white; color: #374151; font-size: 13px; font-weight: 600; cursor: pointer; }
  .ag-group button + button { border-left: 1px solid #e5e7eb; }
  .ag-group button:hover { background: #f3f4f6; }
  .ag-group button.active { background: ${BRAND}; color: white; }
  .ag-icon-btn { width: 36px; height: 36px; border-radius: 10px; border: 1px solid #e5e7eb; background: white; color: #374151; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; font-size: 15px; }
  .ag-icon-btn:hover { background: #f3f4f6; }
  .ag-primary { padding: 9px 18px; border-radius: 10px; border: none; background: ${BRAND}; color: white; font-weight: 700; font-size: 14px; cursor: pointer; box-shadow: 0 4px 12px rgba(176,0,90,0.3); }
  .ag-primary:disabled { opacity: 0.6; cursor: default; }
  .ag-secondary { padding: 9px 18px; border-radius: 10px; border: 1px solid #e5e7eb; background: white; color: #374151; font-weight: 600; font-size: 14px; cursor: pointer; }
  .ag-danger { padding: 9px 14px; border-radius: 10px; border: 1px solid #fecaca; background: #fef2f2; color: #dc2626; font-weight: 700; font-size: 14px; cursor: pointer; }
  .ag-fab { display: none; }

  /* Modales: centrados en PC, hoja inferior en celular */
  .ag-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 1050; display: flex; align-items: center; justify-content: center; padding: 16px; }
  .ag-modal { background: white; border-radius: 16px; width: 520px; max-width: 100%; max-height: 92vh; display: flex; flex-direction: column; box-shadow: 0 20px 60px rgba(0,0,0,0.2); }
  .ag-modal-head { padding: 18px 22px 12px; display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #f3f4f6; }
  .ag-modal-head h3 { margin: 0; font-size: 18px; font-weight: 800; color: #111827; }
  .ag-close { background: none; border: none; font-size: 20px; color: #9ca3af; cursor: pointer; padding: 4px; line-height: 1; }
  .ag-modal-body { padding: 18px 22px; overflow-y: auto; flex: 1; }
  .ag-modal-foot { padding: 14px 22px 18px; border-top: 1px solid #f3f4f6; display: flex; gap: 10px; justify-content: flex-end; align-items: center; }

  .ag-field { margin-bottom: 18px; position: relative; }
  .ag-label { display: block; font-size: 12px; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: 0.03em; margin-bottom: 6px; }
  .ag-input { width: 100%; box-sizing: border-box; padding: 10px 12px; border: 1px solid #e5e7eb; border-radius: 10px; font-size: 15px; color: #111827; background: white; outline: none; }
  .ag-input:focus { border-color: ${BRAND}; box-shadow: 0 0 0 3px rgba(176,0,90,0.1); }
  .ag-row { display: grid; grid-template-columns: 1.4fr 1fr 1fr; gap: 10px; }

  .ag-suggest { position: absolute; left: 0; right: 0; top: 100%; margin-top: 4px; background: white; border: 1px solid #e5e7eb; border-radius: 10px; box-shadow: 0 8px 24px rgba(0,0,0,0.12); z-index: 5; overflow: hidden; }
  .ag-suggest button { display: block; width: 100%; text-align: left; padding: 10px 12px; border: none; background: white; font-size: 14px; cursor: pointer; }
  .ag-suggest button:hover { background: #fdf2f8; }
  .ag-hint { font-size: 12px; color: #9ca3af; margin-top: 4px; }

  /* Servicios: lista de elegidos + buscador */
  .ag-svc-row { display: flex; align-items: center; gap: 10px; padding: 10px 12px; margin-bottom: 8px; background: #fdf2f8; border: 1px solid #fbcfe8; border-radius: 10px; }
  .ag-svc-name { flex: 1; font-size: 14px; font-weight: 700; color: #111827; }
  .ag-svc-price { font-size: 14px; font-weight: 600; color: #6b7280; }
  .ag-svc-staff { margin-top: 6px; width: 100%; padding: 6px 8px; border: 1px solid #e5e7eb; border-radius: 8px; background: white; font-size: 13px; font-weight: 600; color: #374151; }
  .ag-staff-bar { display: flex; gap: 6px; overflow-x: auto; padding-bottom: 4px; margin-bottom: 10px; scrollbar-width: none; }
  .ag-staff-bar::-webkit-scrollbar { display: none; }
  .ag-staff-chip { flex-shrink: 0; display: inline-flex; align-items: center; gap: 6px; padding: 5px 11px; border: 1px solid #e5e7eb; border-radius: 20px; background: white; font-size: 12px; font-weight: 700; color: #374151; cursor: pointer; }
  .ag-staff-chip i { width: 10px; height: 10px; border-radius: 50%; }
  .ag-staff-chip.on { background: #111827; border-color: #111827; color: white; }
  .ag-item-task { display: flex; align-items: center; justify-content: space-between; gap: 8px; font-size: 13px; color: #374151; margin-top: 4px; }
  .ag-svc-remove { border: none; background: none; color: #9ca3af; font-size: 16px; cursor: pointer; padding: 2px 4px; }
  .ag-svc-list { margin-top: 6px; border: 1px solid #e5e7eb; border-radius: 10px; max-height: 220px; overflow-y: auto; }
  .ag-svc-list button { display: flex; justify-content: space-between; gap: 10px; width: 100%; padding: 11px 12px; border: none; border-bottom: 1px solid #f3f4f6; background: white; font-size: 14px; color: #111827; text-align: left; cursor: pointer; }
  .ag-svc-list button:hover, .ag-svc-list button:active { background: #fdf2f8; }
  .ag-svc-list small { color: #6b7280; font-size: 13px; font-weight: 600; white-space: nowrap; }
  .ag-add-more { width: 100%; padding: 10px; border: 1px dashed ${BRAND}; border-radius: 10px; background: white; color: ${BRAND}; font-weight: 700; font-size: 14px; cursor: pointer; }

  /* Vista celular: franja de la semana + lista de citas del día */
  .ag-m-head { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
  .ag-m-head h2 { flex: 1; margin: 0; font-size: 18px; font-weight: 800; color: #111827; }
  .ag-m-head h2::first-letter { text-transform: uppercase; }
  .ag-week { display: flex; align-items: center; gap: 4px; margin-bottom: 12px; }
  .ag-week-arrow { border: none; background: none; color: #6b7280; font-size: 16px; padding: 6px 2px; cursor: pointer; }
  .ag-week-days { flex: 1; display: grid; grid-template-columns: repeat(7, 1fr); gap: 4px; }
  .ag-day { border: none; background: #f3f4f6; border-radius: 12px; padding: 6px 0 5px; display: flex; flex-direction: column; align-items: center; cursor: pointer; color: #374151; }
  .ag-day span { font-size: 10px; font-weight: 700; text-transform: uppercase; color: #9ca3af; }
  .ag-day strong { font-size: 16px; line-height: 1.2; }
  .ag-day i { width: 5px; height: 5px; border-radius: 50%; background: transparent; margin-top: 2px; }
  .ag-day.has i { background: ${BRAND}; }
  .ag-day.today strong { color: ${BRAND}; }
  .ag-day.sel { background: ${BRAND}; color: white; }
  .ag-day.sel span { color: rgba(255,255,255,0.8); }
  .ag-day.sel strong { color: white; }
  .ag-day.sel.has i { background: white; }
  .ag-list { flex: 1; overflow-y: auto; padding-bottom: 90px; }
  .ag-item { display: flex; gap: 12px; width: 100%; text-align: left; padding: 12px; margin-bottom: 10px; background: white; border: 1px solid #e5e7eb; border-left: 5px solid; border-radius: 12px; cursor: pointer; box-shadow: 0 1px 4px rgba(0,0,0,0.04); }
  .ag-item-time { min-width: 48px; display: flex; flex-direction: column; font-size: 12px; color: #9ca3af; }
  .ag-item-time strong { font-size: 16px; color: #111827; }
  .ag-item-main { flex: 1; min-width: 0; }
  .ag-item-name { font-size: 16px; font-weight: 800; color: #111827; }
  .ag-item-svc { font-size: 13px; color: #4b5563; margin-top: 2px; }
  .ag-item-meta { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 6px; }
  .ag-badge { font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 20px; }
  .ag-empty { text-align: center; color: #9ca3af; padding: 40px 16px; }
  .ag-empty div { font-size: 40px; margin-bottom: 6px; }

  .ag-chip { padding: 8px 12px; border-radius: 20px; border: 1px solid #e5e7eb; background: white; color: #374151; font-size: 13px; font-weight: 600; cursor: pointer; display: inline-flex; gap: 6px; align-items: center; }
  .ag-chip small { color: #9ca3af; font-weight: 600; }
  .ag-chip.on { background: ${BRAND}; border-color: ${BRAND}; color: white; }
  .ag-chip.on small { color: rgba(255,255,255,0.8); }

  .ag-money { display: flex; justify-content: space-between; align-items: center; gap: 12px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px 14px; }
  .ag-money-item { font-size: 11px; font-weight: 700; color: #6b7280; text-transform: uppercase; }
  .ag-money-item div { font-size: 17px; font-weight: 800; color: #111827; text-transform: none; }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    .ag-page { padding: 0; height: calc(100dvh - 60px); }
    .ag-card { border-radius: 0; padding: 10px; box-shadow: none; }
    .ag-card { padding: 12px 12px 0; }
    .ag-fab { display: flex; position: fixed; right: 18px; bottom: 22px; width: 58px; height: 58px; border-radius: 50%; border: none; background: ${BRAND}; color: white; font-size: 30px; line-height: 1; align-items: center; justify-content: center; box-shadow: 0 6px 18px rgba(176,0,90,0.45); z-index: 1000; cursor: pointer; }
    .ag-overlay { align-items: flex-end; padding: 0; }
    .ag-modal { width: 100%; max-height: 94dvh; border-radius: 18px 18px 0 0; }
    .ag-modal-foot { flex-wrap: wrap; }
    .ag-modal-foot .ag-primary, .ag-modal-foot .ag-secondary { flex: 1; padding: 13px; }
    .ag-input { font-size: 16px; } /* evita el zoom automático de iOS */
  }
`;

const STATUS_COLORS = {
  SCHEDULED:   '#3b82f6',
  IN_PROGRESS: '#f59e0b',
  COMPLETED:   '#10b981',
  CANCELLED:   '#ef4444',
};

const STATUS_LABELS = {
  SCHEDULED:   'Programada',
  IN_PROGRESS: 'En curso',
  COMPLETED:   'Completada',
  CANCELLED:   'Cancelada',
};

const money = (n) => `$${(parseFloat(n) || 0).toFixed(2)}`;
const fullName = (p) => p ? `${p.first_name || ''} ${p.last_name || ''}`.trim() : '';
const staffName = (u) => u ? (u.first_name || u.username || 'Encargada') : 'Sin encargada';
const staffColor = (u) => u?.color || '#9ca3af';
const serviceStaffId = (s) => String(s.doctor || s.doctor_details?.id || '');
// Color de la cita = color de la encargada del primer servicio asignado
const appointmentColor = (app) =>
  (app.services || []).find(s => s.doctor_details)?.doctor_details?.color || STATUS_COLORS[app.status] || STATUS_COLORS.SCHEDULED;

/* ─── Etiqueta con el color de la encargada ─── */
const StaffTag = ({ user, small }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: '4px', maxWidth: '100%',
    fontSize: small ? '9.5px' : '12px', fontWeight: 700, color: 'white',
    background: staffColor(user), padding: small ? '1px 5px' : '2px 8px', borderRadius: '20px',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  }}>{staffName(user)}</span>
);

const servicesTotal = (services = []) =>
  services.reduce((sum, s) => sum + parseFloat(s.service_details?.price || 0), 0);

/* ─── Cabecera de día ─── */
const CustomDayHeader = ({ date }) => (
  <div style={{ textAlign: 'center', padding: '4px 0' }}>
    <div style={{ fontSize: '18px', fontWeight: '700', color: '#111827', lineHeight: 1 }}>{moment(date).format('DD')}</div>
    <div style={{ fontSize: '10px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', marginTop: '2px' }}>{moment(date).format('ddd')}</div>
  </div>
);

/* ─── Evento en el calendario: nombre, abono y servicios ─── */
const CustomEvent = ({ event }) => {
  const app = event.resource || {};
  const name = fullName(app.patient_details) || 'Paciente';
  const services = (app.services || []).map(s => s.service_details?.name || 'Servicio').join(' · ');
  const abono = parseFloat(app.advance_payment || 0);
  const staff = [];
  (app.services || []).forEach(s => {
    if (s.doctor_details && !staff.some(u => u.id === s.doctor_details.id)) staff.push(s.doctor_details);
  });
  const tooltip = `${name}\n${(app.services || []).map(s => `${s.service_details?.name || 'Servicio'} → ${staffName(s.doctor_details)}`).join('\n')}${abono > 0 ? `\nAbono: ${money(abono)}` : ''}`;

  return (
    <div title={tooltip} style={{ lineHeight: 1.25, overflow: 'hidden', height: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '4px' }}>
        <span style={{ fontWeight: '800', fontSize: '11.5px', color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
        {abono > 0 && (
          <span style={{ fontSize: '9px', fontWeight: '800', color: '#15803d', background: '#dcfce7', padding: '1px 4px', borderRadius: '4px', flexShrink: 0 }}>${abono.toFixed(0)}</span>
        )}
      </div>
      {services && (
        <div style={{ fontSize: '10px', color: '#475569', fontWeight: '600', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{services}</div>
      )}
      {staff.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginTop: '2px' }}>
          {staff.map(u => <StaffTag key={u.id} user={u} small />)}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════
   COMPONENTE PRINCIPAL
═══════════════════════════════════════════ */
const Agenda = () => {
  const { user } = useContext(AuthContext);
  const roleName = user?.role_details?.name;
  const isAdmin = roleName === 'SUPER_ADMIN' || roleName === 'ADMIN_FAST_FOOD' || user?.is_superuser;

  const API_URL = process.env.REACT_APP_FAST_FOOD_SERVICE || process.env.REACT_APP_API_URL || 'http://localhost:8002';

  /* ── Calendario ── */
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState(() => {
    const saved = localStorage.getItem('agendaLastView');
    return ['day', 'week', 'month'].includes(saved) ? saved : (window.innerWidth <= MOBILE_BREAKPOINT ? 'day' : 'week');
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // En celular la vista semana no cabe: se muestra el día
  const view = isMobile && currentView === 'week' ? 'day' : currentView;
  useEffect(() => { localStorage.setItem('agendaLastView', currentView); }, [currentView]);

  /* ── Catálogos ── */
  const [patients, setPatients] = useState([]);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);           // usuarios que pueden ser encargadas
  const [staffFilter, setStaffFilter] = useState(''); // '' = todas

  /* ── Formulario (solo nombre, servicios y abono) ── */
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [patientName, setPatientName] = useState('');
  const [patientId, setPatientId] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [duration, setDuration] = useState(60);
  const [selectedServices, setSelectedServices] = useState([]);
  const [serviceQuery, setServiceQuery] = useState('');
  const [defaultStaffId, setDefaultStaffId] = useState(''); // encargada de la cita (se aplica a los servicios)
  const [showServicePicker, setShowServicePicker] = useState(true);
  const [serviceListOpen, setServiceListOpen] = useState(false);
  const [advancePayment, setAdvancePayment] = useState('');
  const [saving, setSaving] = useState(false);

  /* ── Detalle ── */
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [deleting, setDeleting] = useState(false);

  /* ── Configuración de horario ── */
  const [showConfig, setShowConfig] = useState(false);
  const [config, setConfig] = useState(() => {
    try { return JSON.parse(localStorage.getItem('agendaConfig')) || { startHour: 8, endHour: 20, visibleDays: [1,2,3,4,5] }; }
    catch { return { startHour: 8, endHour: 20, visibleDays: [1,2,3,4,5] }; }
  });
  const [tempConfig, setTempConfig] = useState(config);

  /* ── Data fetching ── */
  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/agenda/appointments/', { baseURL: API_URL });
      const data = res.data?.results || res.data || [];
      setEvents(data.map(app => ({
        id: app.id,
        title: fullName(app.patient_details) || 'Paciente',
        start: new Date(app.start_time),
        end: new Date(app.end_time),
        resource: app,
      })));
    } catch (e) { console.error('Error citas', e); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFormData = useCallback(async () => {
    try {
      const [patRes, prodRes, catRes, usersRes] = await Promise.all([
        api.get('/api/customers/admin/list/?page_size=1000', { baseURL: API_URL }),
        api.get('/api/menu/products/', { baseURL: API_URL }),
        api.get('/api/menu/categories/', { baseURL: API_URL }),
        api.get('/api/users/', { baseURL: API_URL }).catch(() => ({ data: [] })),
      ]);
      const users = usersRes.data?.results || usersRes.data || [];
      setStaff(users.filter(u => u.is_active !== false).sort((a, b) => staffName(a).localeCompare(staffName(b))));
      const products = prodRes.data?.results || prodRes.data || [];
      const categories = catRes.data?.results || catRes.data || [];
      // Los artículos de la categoría "Productos" no son servicios agendables
      const productosCat = categories.find(c => c.name.toLowerCase() === 'productos');
      const isProduct = (p) => {
        if (!productosCat) return false;
        const cat = categories.find(c => c.id === p.category);
        return p.category === productosCat.id || cat?.parent === productosCat.id;
      };
      setPatients(patRes.data?.data?.customers || patRes.data?.results || patRes.data || []);
      setServices(products.filter(p => !isProduct(p)));
    } catch (e) { console.error('Error form data', e); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchAppointments(); fetchFormData(); }, [fetchAppointments, fetchFormData]);

  /* ── Abrir formulario ── */
  const openNew = (start, end) => {
    const s = moment(start);
    const mins = end ? moment(end).diff(s, 'minutes') : 0;
    setEditingId(null);
    setPatientName(''); setPatientId('');
    setDate(s.format('YYYY-MM-DD'));
    setTime(s.format('HH:mm'));
    setDuration(mins > 0 ? mins : 60);
    setSelectedServices([]);
    setServiceQuery(''); setShowServicePicker(true);
    setDefaultStaffId(staffFilter); // si se está viendo a una encargada, la cita queda para ella
    setAdvancePayment('');
    setShowModal(true);
  };

  // Nueva cita en el día que se está viendo: hoy a la próxima media hora, otro día a la hora de apertura
  const openNewNow = () => {
    const now = moment();
    openNew(moment(currentDate).isSame(now, 'day')
      ? now.add(30 - (now.minute() % 30), 'minutes').seconds(0)
      : moment(currentDate).hour(config.startHour).minute(0).seconds(0));
  };

  const openEdit = (ev) => {
    const app = ev.resource;
    const s = moment(app.start_time);
    setEditingId(app.id);
    setPatientName(fullName(app.patient_details));
    setPatientId(app.patient || app.patient_details?.id || '');
    setDate(s.format('YYYY-MM-DD'));
    setTime(s.format('HH:mm'));
    setDuration(Math.max(15, moment(app.end_time).diff(s, 'minutes')) || 60);
    // Se conservan doctor y notas que tuviera la cita aunque ya no se pidan
    setSelectedServices((app.services || []).map(sv => ({
      service_id: sv.service || sv.service_details?.id,
      name: sv.service_details?.name,
      price: sv.service_details?.price,
      doctor_id: sv.doctor || null,
      notes: sv.notes || '',
    })));
    setServiceQuery(''); setShowServicePicker(false);
    setDefaultStaffId(serviceStaffId((app.services || []).find(sv => sv.doctor || sv.doctor_details) || {}));
    setAdvancePayment(parseFloat(app.advance_payment || 0) || '');
    setSelectedEvent(null);
    setShowModal(true);
  };

  const addService = (srv) => {
    // El nuevo servicio queda para la encargada elegida en la cita
    setSelectedServices(prev => [...prev, {
      service_id: srv.id, name: srv.name, price: srv.price,
      doctor_id: defaultStaffId || null, notes: '',
    }]);
    setServiceQuery('');
    setServiceListOpen(false);
    setShowServicePicker(false);
  };

  const removeService = (index) => {
    const next = selectedServices.filter((_, i) => i !== index);
    setSelectedServices(next);
    if (!next.length) setShowServicePicker(true);
  };

  /* ── Guardar ── */
  const handleSave = async () => {
    const name = patientName.trim();
    if (!name) { alert('Escribe el nombre del paciente'); return; }
    if (!selectedServices.length) { alert('Selecciona al menos un servicio'); return; }
    if (!date || !time) { alert('Verifica la fecha y hora'); return; }

    const start = moment(`${date} ${time}`, 'YYYY-MM-DD HH:mm');
    const payload = {
      ...(patientId ? { patient: patientId } : { patient_name: name }),
      start_time: start.toISOString(),
      end_time: start.clone().add(duration, 'minutes').toISOString(),
      advance_payment: parseFloat(advancePayment) || 0,
      services_data: selectedServices.map(s => ({ service_id: s.service_id, doctor_id: s.doctor_id, notes: s.notes })),
    };

    setSaving(true);
    try {
      if (editingId) {
        await api.patch(`/api/agenda/appointments/${editingId}/`, payload, { baseURL: API_URL });
      } else {
        await api.post('/api/agenda/appointments/', { ...payload, status: 'SCHEDULED' }, { baseURL: API_URL });
      }
      setShowModal(false);
      setEditingId(null);
      fetchAppointments();
      if (!patientId) fetchFormData(); // el paciente pudo haberse creado
    } catch (e) {
      console.error(e);
      alert('Error al guardar la cita');
    } finally {
      setSaving(false);
    }
  };

  /* ── Estado / eliminar ── */
  const handleUpdateStatus = async (newStatus) => {
    if (!selectedEvent) return;
    try {
      await api.patch(`/api/agenda/appointments/${selectedEvent.id}/`, { status: newStatus }, { baseURL: API_URL });
      setSelectedEvent({ ...selectedEvent, resource: { ...selectedEvent.resource, status: newStatus } });
      fetchAppointments();
    } catch (e) {
      console.error(e);
      alert('Error al actualizar el estado de la cita');
    }
  };

  const handleDelete = async () => {
    if (!selectedEvent) return;
    if (!window.confirm('¿Eliminar esta cita? Esta acción no se puede deshacer.')) return;
    setDeleting(true);
    try {
      await api.delete(`/api/agenda/appointments/${selectedEvent.id}/`, { baseURL: API_URL });
      setSelectedEvent(null);
      fetchAppointments();
    } catch (e) {
      console.error('Error al eliminar cita:', e);
      alert('Error al eliminar la cita');
    } finally {
      setDeleting(false);
    }
  };

  /* ── Config ── */
  const saveConfig = () => {
    setConfig(tempConfig);
    localStorage.setItem('agendaConfig', JSON.stringify(tempConfig));
    setShowConfig(false);
  };
  const toggleDay = (d) => {
    const days = tempConfig.visibleDays || [1,2,3,4,5];
    setTempConfig({ ...tempConfig, visibleDays: days.includes(d) ? days.filter(x => x !== d).sort() : [...days, d].sort() });
  };

  const hiddenDaysCss = () => {
    const show = config.visibleDays || [1,2,3,4,5];
    return [0,1,2,3,4,5,6].filter(d => !show.includes(d)).map(d => {
      // En locale 'es' la semana empieza el lunes: domingo es la columna 7
      const col = d === 0 ? 7 : d;
      return `.rbc-time-view .rbc-time-header-content .rbc-header:nth-child(${col}){display:none!important}
.rbc-time-view .rbc-time-content .rbc-day-slot:nth-child(${col + 1}){display:none!important}`;
    }).join('\n');
  };

  /* ── Estilo de eventos ── */
  const eventStyleGetter = (event) => {
    const color = event.resource?.services?.find(s => s.doctor_details?.color)?.doctor_details?.color
      || STATUS_COLORS[event.resource?.status] || STATUS_COLORS.SCHEDULED;
    return {
      style: {
        background: `linear-gradient(135deg, #ffffff 30%, ${color}22 100%)`,
        border: `1px solid ${color}55`,
        borderLeft: `4px solid ${color}`,
        color: '#0f172a',
        opacity: event.resource?.status === 'CANCELLED' ? 0.55 : 1,
      }
    };
  };

  /* ── Exportar día a imagen ── */
  const exportAgendaAsImage = () => {
    const el = document.getElementById('agenda-export-template');
    if (!el) return;
    html2canvas(el, { scale: 2, useCORS: true, backgroundColor: '#ffffff' }).then(canvas => {
      const link = document.createElement('a');
      link.download = `Agenda_${moment(currentDate).format('YYYY_MM_DD')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    });
  };

  /* ── Navegación ── */
  const navigate = (action) => {
    if (action === 'TODAY') { setCurrentDate(new Date()); return; }
    setCurrentDate(moment(currentDate).add(action === 'NEXT' ? 1 : -1, view).toDate());
  };

  const titleLabel = view === 'day'
    ? moment(currentDate).format('dddd D [de] MMMM')
    : moment(currentDate).format('MMMM YYYY');
  const subtitleLabel = view === 'week'
    ? `Semana del ${moment(currentDate).startOf('week').format('D MMM')}`
    : view === 'day' ? moment(currentDate).format('YYYY') : '';

  /* ── Derivados del formulario ── */
  const nameSuggestions = useMemo(() => {
    const q = patientName.trim().toLowerCase();
    if (q.length < 2 || patientId) return [];
    return patients.filter(p => fullName(p).toLowerCase().includes(q)).slice(0, 5);
  }, [patientName, patientId, patients]);

  const visibleServices = useMemo(() => {
    const q = serviceQuery.trim().toLowerCase();
    return services.filter(s => !selectedServices.some(x => x.service_id === s.id) && (!q || s.name.toLowerCase().includes(q)));
  }, [serviceQuery, services, selectedServices]);

  const formTotal = selectedServices.reduce((sum, s) => sum + parseFloat(s.price || 0), 0);
  const formPending = Math.max(0, formTotal - (parseFloat(advancePayment) || 0));
  const durationOptions = DURATIONS.includes(duration) ? DURATIONS : [...DURATIONS, duration].sort((a, b) => a - b);

  const chooseStaff = (id) => {
    setDefaultStaffId(id);
    setSelectedServices(prev => prev.map(s => ({ ...s, doctor_id: id || null })));
  };

  const setServiceStaff = (index, doctorId) =>
    setSelectedServices(prev => prev.map((s, i) => i === index ? { ...s, doctor_id: doctorId || null } : s));

  const visibleEvents = staffFilter
    ? events.filter(e => (e.resource?.services || []).some(s => serviceStaffId(s) === staffFilter))
    : events;

  const dayEvents = visibleEvents
    .filter(e => moment(e.start).isSame(currentDate, 'day'))
    .sort((a, b) => a.start - b.start);

  // Filtro por encargada: cada una con su color
  const staffFilterBar = staff.length > 0 && (
    <div className="ag-staff-bar">
      <button className={`ag-staff-chip ${!staffFilter ? 'on' : ''}`} onClick={() => setStaffFilter('')}>Todas</button>
      {staff.map(u => {
        const on = staffFilter === String(u.id);
        return (
          <button key={u.id} className="ag-staff-chip" onClick={() => setStaffFilter(on ? '' : String(u.id))}
            style={on ? { background: staffColor(u), borderColor: staffColor(u), color: 'white' } : undefined}>
            <i style={{ background: on ? 'white' : staffColor(u) }} />{staffName(u)}
          </button>
        );
      })}
    </div>
  );

  /* ════════════════ RENDER ════════════════ */
  return (
    <div className="ag-page">
      <style>{AGENDA_CSS + '\n' + hiddenDaysCss()}</style>

      <div className="ag-card">
        {isMobile ? (
          /* ── Celular: semana arriba y lista de citas del día ── */
          <>
            <div className="ag-m-head">
              <h2>{moment(currentDate).format('dddd D [de] MMM')}</h2>
              {!moment(currentDate).isSame(moment(), 'day') && (
                <button className="ag-secondary" style={{ padding: '6px 12px' }} onClick={() => setCurrentDate(new Date())}>Hoy</button>
              )}
              <button className="ag-icon-btn" onClick={exportAgendaAsImage} title="Descargar la agenda del día"><i className="bi bi-download" /></button>
            </div>

            <div className="ag-week">
              <button className="ag-week-arrow" onClick={() => setCurrentDate(moment(currentDate).subtract(1, 'week').toDate())} aria-label="Semana anterior"><i className="bi bi-chevron-left" /></button>
              <div className="ag-week-days">
                {Array.from({ length: 7 }, (_, i) => moment(currentDate).startOf('week').add(i, 'days')).map(d => {
                  const has = visibleEvents.some(e => e.resource?.status !== 'CANCELLED' && moment(e.start).isSame(d, 'day'));
                  const cls = ['ag-day', has && 'has', d.isSame(moment(), 'day') && 'today', d.isSame(currentDate, 'day') && 'sel'].filter(Boolean).join(' ');
                  return (
                    <button key={d.format('YYYY-MM-DD')} className={cls} onClick={() => setCurrentDate(d.toDate())}>
                      <span>{d.format('dd').charAt(0)}</span>
                      <strong>{d.format('D')}</strong>
                      <i />
                    </button>
                  );
                })}
              </div>
              <button className="ag-week-arrow" onClick={() => setCurrentDate(moment(currentDate).add(1, 'week').toDate())} aria-label="Semana siguiente"><i className="bi bi-chevron-right" /></button>
            </div>

            {staffFilterBar}

            <div className="ag-list">
              {loading ? (
                <div className="ag-empty">Cargando citas...</div>
              ) : dayEvents.length === 0 ? (
                <div className="ag-empty">
                  <div><i className="bi bi-calendar-x" /></div>
                  <p style={{ margin: '0 0 14px' }}>{staffFilter ? 'Esta encargada no tiene citas este día' : 'No hay citas este día'}</p>
                  <button className="ag-primary" onClick={openNewNow}>+ Agendar cita</button>
                </div>
              ) : dayEvents.map(ev => {
                const app = ev.resource || {};
                const color = STATUS_COLORS[app.status] || STATUS_COLORS.SCHEDULED;
                const abono = parseFloat(app.advance_payment || 0);
                return (
                  <button key={ev.id} className="ag-item" style={{ borderLeftColor: appointmentColor(app), opacity: app.status === 'CANCELLED' ? 0.6 : 1 }} onClick={() => setSelectedEvent(ev)}>
                    <div className="ag-item-time">
                      <strong>{moment(ev.start).format('HH:mm')}</strong>
                      <span>{moment(ev.end).format('HH:mm')}</span>
                    </div>
                    <div className="ag-item-main">
                      <div className="ag-item-name">{fullName(app.patient_details) || 'Paciente'}</div>
                      {(app.services || []).length ? app.services.map((s, i) => (
                        <div key={i} className="ag-item-task">
                          <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>• {s.service_details?.name || 'Servicio'}</span>
                          <StaffTag user={s.doctor_details} />
                        </div>
                      )) : <div className="ag-item-svc">Sin servicios</div>}
                      <div className="ag-item-meta">
                        <span className="ag-badge" style={{ background: `${color}1a`, color }}>{STATUS_LABELS[app.status] || 'Programada'}</span>
                        {abono > 0 && <span className="ag-badge" style={{ background: '#dcfce7', color: '#15803d' }}>Abono {money(abono)}</span>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        ) : (
        <>
        {/* ── PC: barra única de herramientas ── */}
        <div className="ag-toolbar">
          <div className="ag-title">
            <h2>{titleLabel}</h2>
            {subtitleLabel && <small>{subtitleLabel}</small>}
          </div>

          <div className="ag-group">
            <button onClick={() => navigate('PREV')} aria-label="Anterior"><i className="bi bi-chevron-left" /></button>
            <button onClick={() => navigate('TODAY')}>Hoy</button>
            <button onClick={() => navigate('NEXT')} aria-label="Siguiente"><i className="bi bi-chevron-right" /></button>
          </div>

          <div className="ag-group">
            {['day', 'week', 'month'].map(v => (
              <button key={v} className={view === v ? 'active' : ''} onClick={() => setCurrentView(v)}>
                {v === 'day' ? 'Día' : v === 'week' ? 'Semana' : 'Mes'}
              </button>
            ))}
          </div>

          <button className="ag-icon-btn" onClick={exportAgendaAsImage} title="Exportar el día como imagen"><i className="bi bi-download" /></button>
          <button className="ag-icon-btn" onClick={() => { setTempConfig(config); setShowConfig(true); }} title="Configurar horario"><i className="bi bi-gear" /></button>
          <button className="ag-primary" onClick={openNewNow}>+ Nueva cita</button>
        </div>

        {staffFilterBar}

        {loading ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>Cargando agenda...</div>
        ) : (
          <Calendar
            localizer={localizer}
            events={visibleEvents}
            step={30}
            timeslots={1}
            style={{ flex: 1, minHeight: 0 }}
            date={currentDate}
            onNavigate={setCurrentDate}
            view={view}
            onView={setCurrentView}
            views={['day', 'week', 'month']}
            min={new Date(2024, 0, 1, config.startHour, 0, 0)}
            max={new Date(2024, 0, 1, config.endHour, 59, 59)}
            onSelectSlot={(slot) => {
              if (view === 'month') { setCurrentDate(slot.start); setCurrentView('day'); return; }
              openNew(slot.start, slot.end);
            }}
            onSelectEvent={setSelectedEvent}
            selectable
            longPressThreshold={250}
            eventPropGetter={eventStyleGetter}
            components={{
              event: CustomEvent,
              week: { header: CustomDayHeader },
              day: { header: CustomDayHeader },
            }}
            formats={{
              eventTimeRangeFormat: () => null,
              timeGutterFormat: (d, culture, loc) => loc.format(d, 'HH:mm', culture),
            }}
            messages={{ next: 'Sig', previous: 'Ant', today: 'Hoy', month: 'Mes', week: 'Semana', day: 'Día', showMore: n => `+${n} más` }}
          />
        )}
        </>
        )}
      </div>

      {/* Botón flotante en celular */}
      <button className="ag-fab" onClick={openNewNow} aria-label="Nueva cita">+</button>

      {/* ════════ MODAL: NUEVA / EDITAR CITA ════════ */}
      {showModal && (
        <div className="ag-overlay" onClick={() => !saving && setShowModal(false)}>
          <div className="ag-modal" onClick={e => e.stopPropagation()}>
            <div className="ag-modal-head">
              <h3>{editingId ? 'Editar cita' : 'Nueva cita'}</h3>
              <button className="ag-close" onClick={() => setShowModal(false)} aria-label="Cerrar"><i className="bi bi-x-lg" /></button>
            </div>

            <div className="ag-modal-body">
              {/* Nombre */}
              <div className="ag-field">
                <label className="ag-label">Nombre</label>
                <input
                  className="ag-input"
                  value={patientName}
                  autoFocus={!isMobile}
                  placeholder="Nombre del paciente"
                  onChange={e => { setPatientName(e.target.value); setPatientId(''); setShowSuggestions(true); }}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                />
                {showSuggestions && nameSuggestions.length > 0 && (
                  <div className="ag-suggest">
                    {nameSuggestions.map(p => (
                      <button key={p.id} type="button" onMouseDown={e => e.preventDefault()} onClick={() => { setPatientName(fullName(p)); setPatientId(p.id); setShowSuggestions(false); }}>
                        {fullName(p)}{p.cedula && <span style={{ color: '#9ca3af' }}> · {p.cedula}</span>}
                      </button>
                    ))}
                  </div>
                )}
                {patientName.trim() && (
                  <div className="ag-hint">{patientId ? <><i className="bi bi-check-circle-fill" style={{ color: '#10b981' }} /> Paciente registrado</> : 'Paciente nuevo: se registrará solo con el nombre'}</div>
                )}
              </div>

              {/* Encargada */}
              {staff.length > 0 && (
                <div className="ag-field">
                  <label className="ag-label">Encargada</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {staff.map(u => {
                      const on = String(defaultStaffId) === String(u.id);
                      return (
                        <button key={u.id} type="button" className="ag-staff-chip" onClick={() => chooseStaff(on ? '' : String(u.id))}
                          style={{ padding: '8px 14px', fontSize: '14px', ...(on ? { background: staffColor(u), borderColor: staffColor(u), color: 'white' } : {}) }}>
                          <i style={{ background: on ? 'white' : staffColor(u) }} />{staffName(u)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Servicios */}
              <div className="ag-field">
                <label className="ag-label">
                  Servicios {selectedServices.length > 0 && <span style={{ color: BRAND }}>({selectedServices.length})</span>}
                </label>
                {selectedServices.map((s, i) => {
                  const assigned = staff.find(u => String(u.id) === String(s.doctor_id || ''));
                  return (
                    <div key={`${s.service_id}-${i}`} className="ag-svc-row" style={{ borderLeft: `5px solid ${staffColor(assigned)}` }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <span className="ag-svc-name">{s.name || 'Servicio'}</span>
                          <span className="ag-svc-price">{money(s.price)}</span>
                        </div>
                        {/* Con varios servicios, cada uno puede ir a otra encargada */}
                        {selectedServices.length > 1 ? (
                          <select className="ag-svc-staff" value={s.doctor_id ? String(s.doctor_id) : ''} onChange={e => setServiceStaff(i, e.target.value)}>
                            <option value="">Encargada: sin asignar</option>
                            {staff.map(u => <option key={u.id} value={String(u.id)}>Encargada: {`${u.first_name || u.username} ${u.last_name || ''}`.trim()}</option>)}
                          </select>
                        ) : assigned && (
                          <div style={{ marginTop: '4px' }}><StaffTag user={assigned} /></div>
                        )}
                      </div>
                      <button type="button" className="ag-svc-remove" onClick={() => removeService(i)} aria-label="Quitar servicio"><i className="bi bi-x-lg" /></button>
                    </div>
                  );
                })}

                {showServicePicker || !selectedServices.length ? (
                  <>
                    <input
                      className="ag-input"
                      placeholder="Buscar tratamiento..."
                      value={serviceQuery}
                      autoFocus={selectedServices.length > 0}
                      onChange={e => { setServiceQuery(e.target.value); setServiceListOpen(true); }}
                      onFocus={() => setServiceListOpen(true)}
                      onBlur={() => setTimeout(() => setServiceListOpen(false), 200)}
                    />
                    {serviceListOpen && (
                      <div className="ag-svc-list">
                        {visibleServices.map(srv => (
                          <button key={srv.id} type="button" onMouseDown={e => e.preventDefault()} onClick={() => addService(srv)}>
                            <span>{srv.name}</span><small>{money(srv.price)}</small>
                          </button>
                        ))}
                        {!visibleServices.length && <div className="ag-hint" style={{ padding: '10px 12px', margin: 0 }}>No se encontró ese tratamiento</div>}
                      </div>
                    )}
                  </>
                ) : (
                  <button type="button" className="ag-add-more" onClick={() => setShowServicePicker(true)}>+ Agregar otro servicio</button>
                )}
              </div>

              {/* Abono */}
              <div className="ag-field">
                <div className="ag-money">
                  <div className="ag-money-item">Total<div>{money(formTotal)}</div></div>
                  <div className="ag-money-item" style={{ flex: 1, maxWidth: '140px' }}>
                    Abono
                    <input className="ag-input" type="number" inputMode="decimal" min="0" step="0.01" placeholder="0.00" value={advancePayment} onChange={e => setAdvancePayment(e.target.value)} style={{ marginTop: '4px', padding: '8px 10px' }} />
                  </div>
                  <div className="ag-money-item" style={{ textAlign: 'right' }}>Pendiente<div style={{ color: BRAND }}>{money(formPending)}</div></div>
                </div>
              </div>

              {/* Fecha y hora (vienen del espacio elegido en el calendario) */}
              <div className="ag-row">
                <div>
                  <label className="ag-label">Fecha</label>
                  <input className="ag-input" type="date" value={date} onChange={e => setDate(e.target.value)} />
                </div>
                <div>
                  <label className="ag-label">Hora</label>
                  <input className="ag-input" type="time" value={time} onChange={e => setTime(e.target.value)} />
                </div>
                <div>
                  <label className="ag-label">Duración</label>
                  <select className="ag-input" value={duration} onChange={e => setDuration(parseInt(e.target.value, 10))}>
                    {durationOptions.map(m => <option key={m} value={m}>{m < 60 ? `${m} min` : `${m / 60} h`.replace('.5 h', ' h 30')}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div className="ag-modal-foot">
              <button className="ag-secondary" onClick={() => setShowModal(false)} disabled={saving}>Cancelar</button>
              <button className="ag-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : editingId ? 'Guardar cambios' : 'Agendar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════════ MODAL: DETALLE DE CITA ════════ */}
      {selectedEvent && (() => {
        const app = selectedEvent.resource || {};
        const total = servicesTotal(app.services);
        const abono = parseFloat(app.advance_payment || 0);
        return (
          <div className="ag-overlay" onClick={() => setSelectedEvent(null)}>
            <div className="ag-modal" onClick={e => e.stopPropagation()}>
              <div className="ag-modal-head">
                <div>
                  <h3>{fullName(app.patient_details) || 'Paciente'}</h3>
                  <div style={{ fontSize: '13px', color: '#6b7280', fontWeight: 600, textTransform: 'capitalize' }}>
                    {moment(selectedEvent.start).format('ddd D MMM, HH:mm')} – {moment(selectedEvent.end).format('HH:mm')}
                  </div>
                </div>
                <button className="ag-close" onClick={() => setSelectedEvent(null)} aria-label="Cerrar"><i className="bi bi-x-lg" /></button>
              </div>

              <div className="ag-modal-body">
                <div className="ag-field">
                  <label className="ag-label">Estado</label>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {Object.keys(STATUS_LABELS).map(key => {
                      const active = app.status === key;
                      return (
                        <button key={key} onClick={() => handleUpdateStatus(key)} className="ag-chip" style={{
                          background: active ? STATUS_COLORS[key] : 'white',
                          borderColor: STATUS_COLORS[key],
                          color: active ? 'white' : STATUS_COLORS[key],
                        }}>{STATUS_LABELS[key]}</button>
                      );
                    })}
                  </div>
                </div>

                <div className="ag-field">
                  <label className="ag-label">Servicios</label>
                  {(app.services || []).length ? (app.services.map((s, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', padding: '8px 0 8px 10px', borderBottom: '1px solid #f3f4f6', borderLeft: `4px solid ${staffColor(s.doctor_details)}`, fontSize: '14px' }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700, color: '#111827' }}>{s.service_details?.name || 'Servicio'}</div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>Encargada: <StaffTag user={s.doctor_details} /></div>
                      </div>
                      <span style={{ color: '#6b7280', whiteSpace: 'nowrap' }}>{money(s.service_details?.price)}</span>
                    </div>
                  ))) : <div className="ag-hint">Sin servicios.</div>}
                </div>

                {app.notes && (
                  <div className="ag-field">
                    <label className="ag-label">Notas</label>
                    <div style={{ fontSize: '14px', color: '#374151' }}>{app.notes}</div>
                  </div>
                )}

                <div className="ag-money">
                  <div className="ag-money-item">Total<div>{money(total)}</div></div>
                  <div className="ag-money-item">Abono<div style={{ color: '#10b981' }}>{money(abono)}</div></div>
                  <div className="ag-money-item" style={{ textAlign: 'right' }}>Pendiente<div style={{ color: BRAND }}>{money(Math.max(0, total - abono))}</div></div>
                </div>
              </div>

              <div className="ag-modal-foot" style={{ justifyContent: isAdmin ? 'space-between' : 'flex-end' }}>
                {isAdmin && (
                  <button className="ag-danger" onClick={handleDelete} disabled={deleting} title="Eliminar cita">
                    <i className="bi bi-trash" /> {deleting ? 'Eliminando...' : 'Eliminar'}
                  </button>
                )}
                <div style={{ display: 'flex', gap: '10px', flex: isMobile ? 1 : 'initial' }}>
                  <button className="ag-secondary" onClick={() => setSelectedEvent(null)}>Cerrar</button>
                  <button className="ag-primary" onClick={() => openEdit(selectedEvent)}>Editar</button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ════════ MODAL: CONFIGURACIÓN ════════ */}
      {showConfig && (
        <div className="ag-overlay" onClick={() => setShowConfig(false)}>
          <div className="ag-modal" style={{ width: '440px' }} onClick={e => e.stopPropagation()}>
            <div className="ag-modal-head">
              <h3>Horario de la agenda</h3>
              <button className="ag-close" onClick={() => setShowConfig(false)} aria-label="Cerrar"><i className="bi bi-x-lg" /></button>
            </div>
            <div className="ag-modal-body">
              <div className="ag-field">
                <label className="ag-label">Días visibles</label>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'].map((n, i) => (
                    <button key={i} className={`ag-chip ${(tempConfig.visibleDays || []).includes(i) ? 'on' : ''}`} onClick={() => toggleDay(i)}>{n}</button>
                  ))}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                {[{ label: 'Desde', key: 'startHour' }, { label: 'Hasta', key: 'endHour' }].map(({ label, key }) => (
                  <div key={key}>
                    <label className="ag-label">{label}</label>
                    <select className="ag-input" value={tempConfig[key]} onChange={e => setTempConfig({ ...tempConfig, [key]: parseInt(e.target.value, 10) })}>
                      {[...Array(24).keys()].map(h => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
            <div className="ag-modal-foot">
              <button className="ag-secondary" onClick={() => setShowConfig(false)}>Cancelar</button>
              <button className="ag-primary" onClick={saveConfig}>Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Plantilla oculta para exportar el día como imagen ── */}
      <div id="agenda-export-template" style={{ position: 'absolute', top: '-9999px', left: '-9999px', width: '800px', background: '#ffffff', padding: '40px', fontFamily: 'Inter, sans-serif' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #e5e7eb', paddingBottom: '20px', marginBottom: '20px' }}>
          <div>
            <h1 style={{ margin: 0, color: '#111827', fontSize: '28px' }}>Agenda Akash</h1>
            <p style={{ margin: '5px 0 0', color: '#6b7280', fontSize: '16px' }}>{moment(currentDate).format('dddd, D [de] MMMM YYYY')}</p>
          </div>
          <img src={require('../../assets/logo.png')} alt="Akash" style={{ height: '60px', objectFit: 'contain' }} />
        </div>
        {dayEvents.length === 0 ? (
          <p style={{ color: '#6b7280', fontSize: '18px', textAlign: 'center', margin: '40px 0' }}>No hay citas agendadas para este día.</p>
        ) : dayEvents.map(ev => {
          const app = ev.resource;
          const total = servicesTotal(app.services);
          const abono = parseFloat(app.advance_payment || 0);
          return (
            <div key={ev.id} style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', padding: '14px 16px', marginBottom: '12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderLeft: `6px solid ${STATUS_COLORS[app.status] || STATUS_COLORS.SCHEDULED}`, borderRadius: '10px' }}>
              <div style={{ fontSize: '16px', fontWeight: 800, color: BRAND, minWidth: '110px' }}>
                {moment(ev.start).format('HH:mm')} – {moment(ev.end).format('HH:mm')}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '16px', fontWeight: 700, color: '#111827' }}>{fullName(app.patient_details) || 'Paciente'}</div>
                <div style={{ fontSize: '14px', color: '#4b5563', marginTop: '4px' }}>
                  {(app.services || []).map(s => `${s.service_details?.name || 'Servicio'}${s.doctor_details ? ` (${staffName(s.doctor_details)})` : ''}`).join(', ') || 'Consulta general'}
                </div>
              </div>
              <div style={{ textAlign: 'right', fontSize: '13px', color: '#6b7280' }}>
                <div>Abono: <strong style={{ color: '#10b981' }}>{money(abono)}</strong></div>
                <div>Pendiente: <strong style={{ color: BRAND }}>{money(Math.max(0, total - abono))}</strong></div>
              </div>
            </div>
          );
        })}
        <div style={{ marginTop: '30px', textAlign: 'center', color: '#9ca3af', fontSize: '12px' }}>Generado desde el sistema Akash</div>
      </div>
    </div>
  );
};

export default Agenda;
